import {useCallback, useEffect, useRef, useState} from 'react';
import {getArtGenerationAvailability} from '../../../utils/artConsumption';
import {pollArtCreditsUntilUpdated} from '../../../utils/artCreditsSync';

export default function useArtAvailability({enabled = true} = {}) {
  const [availableArts, setAvailableArts] = useState(null);
  const [usedArts, setUsedArts] = useState(0);
  const [totalArts, setTotalArts] = useState(0);
  const totalArtsRef = useRef(0);

  const applyAvailability = useCallback((availability) => {
    const rawAvailable = Number(availability?.available ?? availability?.total);
    setAvailableArts(Number.isFinite(rawAvailable) ? rawAvailable : null);
    setUsedArts(availability?.used ?? 0);
    setTotalArts(availability?.total ?? 0);
    return availability;
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) {
      return null;
    }

    try {
      const availability = await getArtGenerationAvailability(1, {
        allowCampaignGeneration: true,
      });
      return applyAvailability(availability);
    } catch (error) {
      console.error('Error loading art availability:', error);
      setAvailableArts(null);
      return null;
    }
  }, [applyAvailability, enabled]);

  useEffect(() => {
    totalArtsRef.current = totalArts;
  }, [totalArts]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handlePlanUpdate = (event) => {
      const shouldRetry = Boolean(event?.detail?.retry);

      if (!shouldRetry) {
        refresh();
        return;
      }

      pollArtCreditsUntilUpdated({
        getBaselineTotal: () => totalArtsRef.current,
        refresh,
      });
    };

    window.addEventListener('user-plan-count-updated', handlePlanUpdate);
    return () => {
      window.removeEventListener('user-plan-count-updated', handlePlanUpdate);
    };
  }, [enabled, refresh]);

  return {
    availableArts,
    usedArts,
    totalArts,
    refresh,
  };
}
